import { useEffect, useRef, useState } from 'react'
import {
  getFieldErrors,
  recruiterProfileSchema,
  recruiterContactInfoSchema,
  recruitingFocusSchema,
} from '@tnf-recruit/shared'
import { useRecruiterProfile } from './useRecruiterProfile'
import { useAuth } from './useAuth'
import { formatUsPhone } from '../lib/phone'

export interface RecruiterProfileDataState {
  personalInfo: {
    firstName: string
    lastName: string
    phone: string
    email: string
    bio: string
    position: string
  }
  recruitingFocus: {
    events: string[]
    academicRequirements: {
      minGPA?: number
      minSAT?: number
      minACT?: number
    }
  }
}

const emptyProfileData: RecruiterProfileDataState = {
  personalInfo: {
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    bio: '',
    position: '',
  },
  recruitingFocus: {
    events: [],
    // Unset minimums default to `{}` (not 0) so the fields render blank
    // instead of a literal 0 — a recruiter who never set a minimum shouldn't
    // see one.
    academicRequirements: {},
  },
}

function buildRecruiterProfilePayload(data: RecruiterProfileDataState) {
  const ar = data.recruitingFocus.academicRequirements
  // `school` is deliberately absent: it is set from the verified signup
  // selection server-side and can't be changed from here.
  return {
    position: data.personalInfo.position,
    bio: data.personalInfo.bio || undefined,
    contactInfo: {
      firstName: data.personalInfo.firstName,
      lastName: data.personalInfo.lastName,
      phone: data.personalInfo.phone,
      email: data.personalInfo.email,
    },
    recruitingFocus: {
      events: data.recruitingFocus.events || undefined,
      academicRequirements: {
        minGPA: ar.minGPA !== undefined && ar.minGPA >= 0 && ar.minGPA <= 4 ? ar.minGPA : undefined,
        minSAT:
          ar.minSAT !== undefined && ar.minSAT >= 400 && ar.minSAT <= 1600 ? ar.minSAT : undefined,
        minACT:
          ar.minACT !== undefined && ar.minACT >= 1 && ar.minACT <= 36 ? ar.minACT : undefined,
      },
    },
  }
}

const isMissingOrPlaceholderEmail = (value: string) =>
  !value.trim() || value.includes('@placeholder.tnf-recruit.com')

/**
 * Owns all recruiter-profile form state, hydration, validation, and saving.
 * Shared by the onboarding wizard (`RecruiterProfileWizard`) and the
 * single-page edit form (`RecruiterProfileEditForm`).
 */
export function useRecruiterProfileForm() {
  const { user: authUser } = useAuth()
  const { profile, saveProfile } = useRecruiterProfile()
  const [data, setData] = useState<RecruiterProfileDataState>(emptyProfileData)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [saved, setSaved] = useState(false)
  const hydratedRef = useRef(false)

  const authEmail = authUser?.email ?? ''

  useEffect(() => {
    if (!profile.data || hydratedRef.current) return
    hydratedRef.current = true

    const p = profile.data
    setData({
      personalInfo: {
        firstName: p.contactInfo.firstName ?? '',
        lastName: p.contactInfo.lastName ?? '',
        phone: formatUsPhone(p.contactInfo.phone ?? ''),
        email: isMissingOrPlaceholderEmail(p.contactInfo.email ?? '')
          ? authEmail
          : (p.contactInfo.email ?? ''),
        bio: p.bio ?? '',
        position: p.position ?? '',
      },
      recruitingFocus: {
        events: p.recruitingFocus.events ?? [],
        academicRequirements: p.recruitingFocus.academicRequirements ?? {},
      },
    })
  }, [profile.data, authEmail])

  useEffect(() => {
    if (profile.data) return
    setData(prev => ({
      ...prev,
      personalInfo: {
        ...prev.personalInfo,
        email: isMissingOrPlaceholderEmail(prev.personalInfo.email)
          ? authEmail
          : prev.personalInfo.email,
      },
    }))
  }, [profile.data, authEmail])

  const setErrorsForKeys = (prefix: string, keys: string[], errors: Record<string, string>) => {
    setFieldErrors(prev => {
      const next = { ...prev }
      for (const key of keys) {
        const fullPath = prefix ? `${prefix}.${key}` : key
        if (errors[key]) next[fullPath] = errors[key]
        else delete next[fullPath]
      }
      return next
    })
  }

  /** Per-field validation, routed through the same section schemas used for step validation. */
  const validateField = (
    section: 'contactInfo' | 'position' | 'recruitingFocus',
    nextData: RecruiterProfileDataState
  ) => {
    if (section === 'contactInfo') {
      const errors = getFieldErrors(
        recruiterContactInfoSchema,
        buildRecruiterProfilePayload(nextData).contactInfo
      )
      setErrorsForKeys('contactInfo', ['firstName', 'lastName', 'phone', 'email'], errors)
    } else if (section === 'position') {
      setFieldErrors(prev => {
        const next = { ...prev }
        if (!nextData.personalInfo.position.trim()) next.position = 'Position is required'
        else delete next.position
        return next
      })
    } else if (section === 'recruitingFocus') {
      const errors = getFieldErrors(
        recruitingFocusSchema,
        buildRecruiterProfilePayload(nextData).recruitingFocus
      )
      setErrorsForKeys('recruitingFocus', ['events'], errors)
    }
  }

  /** Per-step validation for the wizard — uses the real section schemas directly
   * instead of stuffing dummy values into the other steps to validate one. */
  const validateStep = (step: 0 | 1): boolean => {
    const body = buildRecruiterProfilePayload(data)
    const stepErrors: Record<string, string> = {}

    if (step === 0) {
      const errors = getFieldErrors(recruiterContactInfoSchema, body.contactInfo)
      for (const [k, v] of Object.entries(errors)) stepErrors[`contactInfo.${k}`] = v
      if (!data.personalInfo.position.trim()) stepErrors.position = 'Position is required'
    } else if (step === 1) {
      const errors = getFieldErrors(recruitingFocusSchema, body.recruitingFocus)
      for (const [k, v] of Object.entries(errors)) stepErrors[`recruitingFocus.${k}`] = v
    }

    if (Object.keys(stepErrors).length > 0) {
      setFieldErrors(stepErrors)
      return false
    }
    setFieldErrors({})
    return true
  }

  /** Full-payload validation used by the single-page edit form and the wizard's final save. */
  const validateAll = (): boolean => {
    const body = buildRecruiterProfilePayload(data)
    const errors = getFieldErrors(recruiterProfileSchema, body)

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors)
      return false
    }
    setFieldErrors({})
    return true
  }

  const save = async (): Promise<boolean> => {
    if (!validateAll()) return false
    try {
      const body = buildRecruiterProfilePayload(data)
      await saveProfile.mutateAsync(body)
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
      return true
    } catch {
      return false
    }
  }

  return {
    isLoading: profile.isLoading,
    data,
    setData,
    fieldErrors,
    setFieldErrors,
    validateField,
    validateStep,
    validateAll,
    save,
    isSaving: saveProfile.isPending,
    isSaveHttpError: saveProfile.isError,
    saved,
  }
}

export type RecruiterProfileFormApi = ReturnType<typeof useRecruiterProfileForm>
